import link from "../static/text/link";
import { shouldForceXtLane } from "./xtLaneFlag";

export const PAYMENT_REDIRECT_STORAGE_KEY = "savedPaymentRedirectQuery";

const PAYMENT_REDIRECT_QUERY_KEYS = [
  "active_promo",
  "promo_code",
  "c",
  "promo_price",
  "referral_id",
  "reseller_price",
  "page_source",
  "slug",
  "pkg",
  "package_type",
  "landing_type",
  "target",
  "cms_slug",
  "context",
  "type",
  "tier",
  "country",
  "country_code",
  "original_page_source",
  "landing_page",
  "is_countdown_active",
  "pure_package_id",
  "cms",
  "quiz_type",
  "websiteRequest",
  "checkout_id",
  "xt",
];

const normalizeQueryValue = (value) => {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
};

export const getPaymentRedirectQuery = (query = {}) => {
  const normalizedQuery = {};

  Object.keys(query || {}).forEach((key) => {
    const value = normalizeQueryValue(query[key]);
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (PAYMENT_REDIRECT_QUERY_KEYS.includes(key) || key.indexOf("display_") === 0) {
      normalizedQuery[key] = value;
    }
  });

  return normalizedQuery;
};

export const appendPaymentRedirectQuery = (baseUrl, query = {}, excludedKeys = []) => {
  const params = new URLSearchParams();
  const normalizedQuery = getPaymentRedirectQuery(query);

  Object.keys(normalizedQuery).forEach((key) => {
    if (excludedKeys.includes(key)) {
      return;
    }

    params.append(key, normalizedQuery[key]);
  });

  if (!params.toString()) {
    return baseUrl;
  }

  return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${params.toString()}`;
};

export const buildPaymentRedirectUrl = (packageId, languageCode, query = {}) => {
  const normalizedQuery = getPaymentRedirectQuery(query);
  // Trello 546 Phase 2: force the post-login/register checkout bounce
  // (auth/login/[id].js x2, auth/change-name) onto the xt-aware lane on
  // semi-prod, unless the caller already forwarded an explicit xt value.
  // This call site only ever sees a package id + a flat query object, never
  // the resolved package, so it can't cheaply tell a SINGLE_PURCHASE pick
  // apart from a subscription one, nor the checkout country. The real safety
  // nets are elsewhere: pages/payment/purchases/[id].js's own package-type +
  // country chokepoint is the primary one (it has the fetched package and the
  // resolved country in hand and is the single place every forced-xt hop
  // funnels through before a checkout POST fires), backed up by the backend
  // eligibility fallback on branch wt/xt546-all-landings-be (commit
  // 9bfd4eec1) once that lands — not a client-side guard at this call site.
  if (shouldForceXtLane() && !normalizedQuery.xt) {
    normalizedQuery.xt = "1";
  }
  const paymentUrl = link.mobile.payment(
    packageId,
    languageCode,
    normalizedQuery.referral_id,
    normalizedQuery.active_promo || normalizedQuery.promo_code,
  );

  return appendPaymentRedirectQuery(paymentUrl, normalizedQuery, [
    "referral_id",
    "active_promo",
    "promo_code",
  ]);
};

export const buildLoginRedirectUrl = (packageId, query = {}) => {
  const loginUrl = `/auth/login/${packageId}?state=register`;
  return appendPaymentRedirectQuery(loginUrl, query);
};

const getStringUrl = (value) => (typeof value === "string" && value.trim() ? value : "");

const getActionUrl = (actions) => {
  if (!Array.isArray(actions)) {
    return "";
  }

  const preferredActions = ["AUTH", "REDIRECT_CUSTOMER", "PAY", "CHECKOUT", "VERIFY"];
  const preferredAction = actions.find((item) => {
    const action = item && item.action ? String(item.action).toUpperCase() : "";
    return preferredActions.includes(action) && getStringUrl(item.url || item.href);
  });

  if (preferredAction) {
    return getStringUrl(preferredAction.url || preferredAction.href);
  }

  const firstUrlAction = actions.find((item) => item && getStringUrl(item.url || item.href));
  return firstUrlAction ? getStringUrl(firstUrlAction.url || firstUrlAction.href) : "";
};

export const getCheckoutPaymentUrl = (checkoutData = {}) => {
  if (!checkoutData || typeof checkoutData !== "object") {
    return "";
  }

  const directUrl = getStringUrl(checkoutData.invoice_url)
    || getStringUrl(checkoutData.payment_url)
    || getStringUrl(checkoutData.redirect_url)
    || getStringUrl(checkoutData.checkout_url);

  if (directUrl) {
    return directUrl;
  }

  if (getStringUrl(checkoutData.activation_url)) {
    return checkoutData.activation_url;
  }

  if (checkoutData.activation_url && getStringUrl(checkoutData.activation_url.url)) {
    return checkoutData.activation_url.url;
  }

  return getActionUrl(checkoutData.actions);
};

export const redirectToCheckoutPaymentUrl = (checkoutData, options = {}) => {
  const targetUrl = getCheckoutPaymentUrl(checkoutData);
  const targetWindow = options.windowObject || (typeof window !== "undefined" ? window : null);

  if (!targetUrl || !targetWindow || !targetWindow.location) {
    return false;
  }

  if (options.replace && typeof targetWindow.location.replace === "function") {
    targetWindow.location.replace(targetUrl);
  } else {
    targetWindow.location.href = targetUrl;
  }

  return true;
};
